import {
  type Company,
  type CreateCompanyInput,
  createCompanyInput,
  type EconomicActivity,
  identificationTypeLabel,
  type MeResponse,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { SaveIcon } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { LocationFields } from '@/components/forms/location-fields'
import { ActivityPicker, TaxpayerLookupButton } from '@/components/forms/taxpayer-lookup'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Field, FormSection } from '@/components/ui/field'
import { Input, Textarea } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { LogoCard } from '@/features/settings/logo-card'
import { api } from '@/lib/api'
import { companyQuery } from '@/lib/queries'
import { useCan, useSession } from '@/lib/session'

export const Route = createFileRoute('/_app/settings/company')({
  staticData: { crumb: 'Datos generales' },
  loader: ({ context }) => context.queryClient.ensureQueryData(companyQuery()),
  pendingComponent: () => (
    <Card>
      <CardContent className="grid gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </CardContent>
    </Card>
  ),
  component: CompanySettings,
})

const idTypeOptions = Object.entries(identificationTypeLabel).map(([value, label]) => ({ value, label }))

function toForm(company: Company): CreateCompanyInput {
  return {
    legalName: company.legalName,
    tradeName: company.tradeName,
    identificationType: company.identificationType as CreateCompanyInput['identificationType'],
    identification: company.identification,
    email: company.email,
    phone: company.phone,
    provinceCode: company.provinceCode,
    cantonCode: company.cantonCode,
    districtCode: company.districtCode,
    addressDetail: company.addressDetail,
    economicActivity: company.economicActivity,
    defaultTerms: company.defaultTerms,
    defaultCurrency: company.defaultCurrency,
  }
}

function CompanySettings() {
  const { data: company } = useSuspenseQuery(companyQuery())
  const canManage = useCan('company:manage')
  const queryClient = useQueryClient()
  const [activities, setActivities] = useState<EconomicActivity[]>([])

  const form = useForm<CreateCompanyInput>({
    resolver: zodResolver(createCompanyInput),
    values: toForm(company),
  })
  const { register, control, setValue, formState } = form
  const identification = form.watch('identification')
  const economicActivity = form.watch('economicActivity')

  const save = useMutation({
    mutationFn: (values: CreateCompanyInput) => api.patch<Company>('/company', values),
    onSuccess: async (updated) => {
      queryClient.setQueryData(companyQuery().queryKey, updated)
      form.reset(toForm(updated))
      toast.success('Datos de la compañía actualizados')
      // Los nombres de la compañía también viven en la sesión (selector de compañías).
      useSession.getState().setSession(await api.get<MeResponse>('/auth/me', { global: true }))
    },
  })

  return (
    <div className="grid gap-6">
      <LogoCard company={company} canManage={canManage} />
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <Card>
          <CardContent className="divide-y divide-border">
            <fieldset disabled={!canManage} className="contents">
              <FormSection
                title="Identidad del emisor"
                description="Así aparece tu compañía en cada comprobante electrónico enviado a Hacienda."
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[12rem_1fr]">
                  <Controller
                    control={control}
                    name="identificationType"
                    render={({ field, fieldState }) => (
                      <Field label="Tipo de identificación" error={fieldState.error} required>
                        {(c) => (
                          <Select
                            {...c}
                            value={field.value}
                            onValueChange={field.onChange}
                            options={idTypeOptions}
                          />
                        )}
                      </Field>
                    )}
                  />
                  <Field label="Número de identificación" error={formState.errors.identification} required>
                    {(c) => (
                      <div className="flex gap-2">
                        <Input
                          {...c}
                          {...register('identification')}
                          inputMode="numeric"
                          className="flex-1 font-mono"
                        />
                        <TaxpayerLookupButton
                          size="md"
                          identification={identification}
                          onResult={(info) => {
                            setActivities(info.activities)
                            if (info.name && !form.getValues('legalName')) {
                              setValue('legalName', info.name, { shouldDirty: true })
                            }
                          }}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <Field label="Razón social" error={formState.errors.legalName} required>
                  {(c) => <Input {...c} {...register('legalName')} />}
                </Field>
                <Field
                  label="Nombre comercial"
                  hint="Opcional. Se muestra en el PDF y en el XML como NombreComercial."
                  error={formState.errors.tradeName}
                >
                  {(c) => <Input {...c} {...register('tradeName', { setValueAs: (v) => v || null })} />}
                </Field>
              </FormSection>

              <FormSection
                title="Contacto"
                description="Hacienda y tus clientes verán estos datos en los comprobantes."
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Correo electrónico" error={formState.errors.email} required>
                    {(c) => <Input {...c} {...register('email')} type="email" />}
                  </Field>
                  <Field label="Teléfono" error={formState.errors.phone}>
                    {(c) => (
                      <Input
                        {...c}
                        {...register('phone', { setValueAs: (v) => v || null })}
                        inputMode="tel"
                        placeholder="22223333"
                      />
                    )}
                  </Field>
                </div>
              </FormSection>

              <FormSection title="Ubicación" description="División territorial oficial de Hacienda.">
                <LocationFields
                  control={control}
                  setValue={setValue}
                  names={{ province: 'provinceCode', canton: 'cantonCode', district: 'districtCode' }}
                />
                <Field label="Otras señas" error={formState.errors.addressDetail}>
                  {(c) => (
                    <Textarea
                      {...c}
                      {...register('addressDetail', { setValueAs: (v) => v || null })}
                      rows={2}
                      placeholder="Del parque central, 200 m norte…"
                    />
                  )}
                </Field>
              </FormSection>

              <FormSection
                title="Actividad económica"
                description="Código que se envía como CodigoActividadEmisor. Consultá en Hacienda para elegir entre tus actividades inscritas."
              >
                <Field
                  label="Código de actividad"
                  error={formState.errors.economicActivity}
                  hint="6 dígitos, p. ej. 620100"
                >
                  {(c) => (
                    <Input
                      {...c}
                      {...register('economicActivity', { setValueAs: (v) => v || null })}
                      className="max-w-xs font-mono"
                      inputMode="numeric"
                    />
                  )}
                </Field>
                <ActivityPicker
                  activities={activities}
                  value={economicActivity}
                  onChange={(code) =>
                    setValue('economicActivity', code, { shouldDirty: true, shouldValidate: true })
                  }
                />
              </FormSection>

              <FormSection
                title="Moneda al emitir"
                description="Moneda que se propone al crear un comprobante nuevo; podés cambiarla en cada uno."
              >
                <Controller
                  control={control}
                  name="defaultCurrency"
                  render={({ field }) => (
                    <Field label="Moneda por defecto">
                      {(c) => (
                        <Select
                          {...c}
                          value={field.value ?? 'CRC'}
                          onValueChange={field.onChange}
                          className="max-w-xs"
                          options={[
                            { value: 'CRC', label: '₡ Colones (CRC)' },
                            { value: 'USD', label: '$ Dólares (USD)' },
                            { value: 'EUR', label: '€ Euros (EUR)' },
                          ]}
                        />
                      )}
                    </Field>
                  )}
                />
              </FormSection>

              <FormSection
                title="Términos y condiciones generales"
                description="Texto de respaldo: se propone en los comprobantes cuyo tipo no tiene términos propios en Configuración → Términos y condiciones."
              >
                <Field label="Texto" error={formState.errors.defaultTerms}>
                  {(c) => (
                    <Textarea
                      {...c}
                      {...register('defaultTerms', { setValueAs: (v) => v || null })}
                      rows={4}
                      placeholder="Ej.: Pago a 30 días. Transferencias a la cuenta IBAN CR…"
                    />
                  )}
                </Field>
              </FormSection>
            </fieldset>
          </CardContent>
          {canManage && (
            <CardFooter className="justify-between">
              <span className="text-muted-foreground text-xs">
                {formState.isDirty ? 'Tenés cambios sin guardar' : 'Todo guardado'}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!formState.isDirty}
                  onClick={() => form.reset()}
                >
                  Descartar
                </Button>
                <Button type="submit" loading={save.isPending} disabled={!formState.isDirty}>
                  <SaveIcon /> Guardar cambios
                </Button>
              </div>
            </CardFooter>
          )}
        </Card>
      </form>
    </div>
  )
}
